import React, { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../app/stores/store';
import { ReportQueryParams } from '../../app/models/Reporting';
import { ResponsiveContainer,BarChart,Tooltip,CartesianGrid, XAxis, YAxis, Bar, LineChart, Legend, Line, PieChart, Pie, Cell } from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────
type ActiveReport = 'occupancy' | 'revenue' | 'payments' | 'lostRevenue' | 'clients' | 'bookingsOverview';

const PAYMENT_COLORS = { card: '#3b82f6', cash: '#10b981', unpaid: '#ef4444' };
const CHART_COLORS   = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

// ─── Shared amount formatter ───────────────────────────────────────────────────
const fmt = (n: number | undefined | null) =>
  `💰 ${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ─── Mobile hook ──────────────────────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// ─── Date helpers (same pattern as CalendarStore) ─────────────────────────────
function toDateInput(d: Date) { return d.toLocaleDateString('en-CA'); }
function fromDateInput(s: string) {
  const [year, month, day] = s.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '1.25rem 1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color ?? '#6366f1'}`,
      minWidth: 0,
    }}>
      <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#111827', margin: '0.3rem 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div style={{ borderBottom: '2px solid #f3f4f6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
      <span style={{ fontSize: '1rem', fontWeight: 700, color: '#374151' }}>{title}</span>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
      <div style={{
        width: 40, height: 40, border: '4px solid #e5e7eb',
        borderTopColor: '#6366f1', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📊</div>
      <div style={{ fontWeight: 600 }}>{t('reporting.noData')}</div>
    </div>
  );
}

// ─── Pill badge ───────────────────────────────────────────────────────────────
function PayBadge({ isPayed }: { isPayed: number }) {
  const { t } = useTranslation();
  const map: Record<number, [string, string]> = {
    0: [t('reporting.paymentStatus.unpaid'), '#ef4444'],
    1: [t('reporting.paymentStatus.card'),   '#3b82f6'],
    2: [t('reporting.paymentStatus.cash'),   '#10b981'],
  };
  const [label, color] = map[isPayed] ?? ['Unknown', '#9ca3af'];
  return (
    <span style={{
      background: color + '22', color, border: `1px solid ${color}44`,
      borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700
    }}>{label}</span>
  );
}

// ─── Report panels ────────────────────────────────────────────────────────────

function OccupancyPanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.occupancy;
  if (!d) return <EmptyState />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        {d.assets.map(a => (
          <StatCard
            key={a.assetId}
            label={a.assetName}
            value={`${a.occupancyPercent.toFixed(1)}%`}
            sub={`${a.totalBookings} ${t('reporting.occupancy.totalBookings')} · ${a.venueName}`}
            color={a.occupancyPercent > 80 ? '#ef4444' : a.occupancyPercent > 50 ? '#f59e0b' : '#10b981'}
          />
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <SectionTitle title={t('reporting.occupancy.byDay')} />
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.occupancyByDay} margin={{ top: 5, right: 10, left: -20, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="dayLabel" angle={-45} textAnchor="end" tick={{ fontSize: 10 }} />
            <YAxis domain={[0, 100]} tickFormatter={(v: any) => `${v}%`} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: any) => [`${v}%`, t('reporting.occupancy.occupancyPercent')]} />
            <Bar dataKey="occupancyPercent" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <SectionTitle title={t('reporting.occupancy.peakHours')} />
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={d.peakHours.filter(h => h.bookingCount > 0)} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="hourLabel" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: any) => [v, t('reporting.occupancy.totalBookings')]} />
            <Bar dataKey="bookingCount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RevenuePanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.revenue;
  if (!d) return <EmptyState />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <StatCard label={t('reporting.revenue.totalRevenue')}   value={fmt(d.totalRevenue)}     color="#6366f1" />
        <StatCard label={t('reporting.revenue.baseRevenue')}    value={fmt(d.totalBaseRevenue)}  color="#8b5cf6" />
        <StatCard label={t('reporting.revenue.totalDiscounts')} value={fmt(d.totalDiscounts)}   sub={t('reporting.revenue.givenAway')} color="#f59e0b" />
        <StatCard label={t('reporting.revenue.priceOverrides')} value={fmt(d.totalOverrides)}   color="#ec4899" />
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <SectionTitle title={t('reporting.revenue.trend')} />
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={d.byPeriod} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="periodLabel" angle={-45} textAnchor="end" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: any) => [fmt(v)]} />
            <Legend />
            <Line type="monotone" dataKey="finalRevenue" stroke="#6366f1" strokeWidth={2} dot={false} name={t('reporting.revenue.finalRevenue')} />
            <Line type="monotone" dataKey="baseRevenue"  stroke="#d1d5db" strokeWidth={1.5} dot={false} name={t('reporting.revenue.baseRevenue')} strokeDasharray="4 2" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
        <SectionTitle title={t('reporting.revenue.byAsset')} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {[
                t('reporting.bookings.asset'),
                t('reporting.filters.venue'),
                t('reporting.revenue.baseRevenue'),
                t('reporting.revenue.totalDiscounts'),
                t('reporting.revenue.finalRevenue'),
                t('reporting.revenue.bookingCount'),
              ].map(h => (
                <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.byAsset.map((a, i) => (
              <tr key={a.assetId} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{a.assetName}</td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{a.venueName}</td>
                <td style={{ padding: '0.6rem 0.75rem' }}>{fmt(a.baseRevenue)}</td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#f59e0b' }}>-{fmt(a.discounts)}</td>
                <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#6366f1' }}>{fmt(a.finalRevenue)}</td>
                <td style={{ padding: '0.6rem 0.75rem' }}>{a.bookingCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsPanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.payments;
  if (!d) return <EmptyState />;

  const pieData = [
    { name: t('reporting.payments.card'),   value: d.totalCard },
    { name: t('reporting.payments.cash'),   value: d.totalCash },
    { name: t('reporting.payments.unpaid'), value: d.totalUnpaid },
  ].filter(x => x.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <StatCard label={t('reporting.payments.totalCollected')} value={fmt(d.totalPaid)}   sub={`${d.countPaid} ${t('reporting.bookings.totalBookings')}`}   color="#10b981" />
        <StatCard label={t('reporting.payments.cash')}           value={fmt(d.totalCash)}   sub={`${d.countCash} ${t('reporting.bookings.totalBookings')}`}    color="#6ee7b7" />
        <StatCard label={t('reporting.payments.card')}           value={fmt(d.totalCard)}   sub={`${d.countCard} ${t('reporting.bookings.totalBookings')}`}    color="#3b82f6" />
        <StatCard label={t('reporting.payments.unpaid')}         value={fmt(d.totalUnpaid)} sub={`${d.countUnpaid} ${t('reporting.bookings.totalBookings')}`}  color="#ef4444" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.5fr)', gap: '1rem' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <SectionTitle title={t('reporting.payments.methods')} />
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {pieData.map((_, idx) => (
                  <Cell key={idx} fill={[PAYMENT_COLORS.card, PAYMENT_COLORS.cash, PAYMENT_COLORS.unpaid][idx]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: any) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <SectionTitle title={t('reporting.payments.overTime')} />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={d.byPeriod} margin={{ top: 5, right: 10, left: -20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="periodLabel" angle={-30} textAnchor="end" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: any) => fmt(v)} />
              <Legend />
              <Bar dataKey="card"   stackId="a" fill={PAYMENT_COLORS.card}   name={t('reporting.payments.card')} />
              <Bar dataKey="cash"   stackId="a" fill={PAYMENT_COLORS.cash}   name={t('reporting.payments.cash')} />
              <Bar dataKey="unpaid" stackId="a" fill={PAYMENT_COLORS.unpaid} name={t('reporting.payments.unpaid')} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {d.unpaidBookings.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <SectionTitle title={`${t('reporting.payments.unpaidBookings')} (${d.unpaidBookings.length})`} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#fef2f2' }}>
                {[
                  t('reporting.bookings.customer'),
                  t('reporting.clients.phone'),
                  t('reporting.bookings.asset'),
                  t('reporting.bookings.date'),
                  t('reporting.payments.amount'),
                  t('reporting.payments.daysAgo'),
                ].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.unpaidBookings.map((b, i) => (
                <tr key={b.bookingId} style={{ borderTop: '1px solid #fee2e2', background: i % 2 === 0 ? '#fff' : '#fff7f7' }}>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{b.customerFullName || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{b.customerPhone}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{b.assetName}</td>
                  <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>
                    {new Date(b.dateTimeStart).toLocaleDateString('en-GB')}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#ef4444' }}>{fmt(b.amount)}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    <span style={{ background: b.daysSinceBooking > 7 ? '#fef2f2' : '#fef9c3', color: b.daysSinceBooking > 7 ? '#ef4444' : '#92400e', borderRadius: 20, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
                      {b.daysSinceBooking}d
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Lost Revenue Panel ───────────────────────────────────────────────────────
function LostRevenuePanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.lostRevenue;
  if (!d) return <EmptyState />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Top summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <StatCard label={t('reporting.lostRevenue.actualRevenue')}    value={fmt(d.totalActualRevenue)}    color="#10b981" />
        <StatCard label={t('reporting.lostRevenue.potentialRevenue')} value={fmt(d.totalPotentialRevenue)} color="#6366f1" />
        <StatCard label={t('reporting.lostRevenue.totalLost')}        value={fmt(d.totalLostRevenue)}      color="#ef4444" />
        <StatCard label={t('reporting.lostRevenue.overallUtilization')} value={`${d.overallUtilization.toFixed(1)}%`} color="#f59e0b" />
      </div>

      {/* Lost revenue breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div style={{
          background: '#fff', borderRadius: 12, padding: '1.1rem 1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('reporting.lostRevenue.lostToDiscounts')}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0.25rem 0 0.2rem', color: '#111827' }}>
            {fmt(d.totalLostToDiscounts)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {t('reporting.lostRevenue.lostToDiscountsSub')}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: 12, padding: '1.1rem 1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '4px solid #ec4899'
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('reporting.lostRevenue.lostToOverrides')}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0.25rem 0 0.2rem', color: '#111827' }}>
            {fmt(d.totalLostToOverrides)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {t('reporting.lostRevenue.lostToOverridesSub')}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: 12, padding: '1.1rem 1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '4px solid #8b5cf6'
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('reporting.lostRevenue.lostToPartial')}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0.25rem 0 0.2rem', color: '#111827' }}>
            {fmt(d.totalLostToPartial)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {t('reporting.lostRevenue.lostToPartialSub')}
          </div>
        </div>
      </div>

      {/* Horizontal bar chart: actual vs lost per asset */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <SectionTitle title={t('reporting.lostRevenue.chartTitle')} />
        <ResponsiveContainer width="100%" height={Math.max(160, d.byAsset.length * 48)}>
          <BarChart data={d.byAsset} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
            <YAxis type="category" dataKey="assetName" tick={{ fontSize: 11 }} width={110} />
            <Tooltip formatter={(v: any) => [fmt(v)]} />
            <Legend />
            <Bar dataKey="actualRevenue"   fill="#10b981" name={t('reporting.lostRevenue.legendActual')}    stackId="a" />
            <Bar dataKey="lostToDiscounts" fill="#fbbf24" name={t('reporting.lostRevenue.legendDiscounts')} stackId="a" />
            <Bar dataKey="lostToOverrides" fill="#f472b6" name={t('reporting.lostRevenue.legendOverrides')} stackId="a" />
            <Bar dataKey="lostToPartial"   fill="#c4b5fd" name={t('reporting.lostRevenue.legendPartial')}   stackId="a" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-asset detail table */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
        <SectionTitle title={t('reporting.lostRevenue.detailByAsset')} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {[
                t('reporting.lostRevenue.colAsset'),
                t('reporting.lostRevenue.colVenue'),
                t('reporting.lostRevenue.colBookings'),
                t('reporting.lostRevenue.colActual'),
                t('reporting.lostRevenue.colDiscounts'),
                t('reporting.lostRevenue.colOverrides'),
                t('reporting.lostRevenue.colPartial'),
                t('reporting.lostRevenue.colTotalLost'),
                t('reporting.lostRevenue.colUtil'),
              ].map(h => (
                <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.byAsset.map((a, i) => (
              <tr key={a.assetId} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff' }}>
                <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{a.assetName}</td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{a.venueName}</td>
                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>{a.bookingCount}</td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#10b981', fontWeight: 600 }}>{fmt(a.actualRevenue)}</td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#f59e0b' }}>
                  {a.lostToDiscounts > 0 ? `-${fmt(a.lostToDiscounts)}` : '—'}
                </td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#ec4899' }}>
                  {a.lostToOverrides > 0 ? `-${fmt(a.lostToOverrides)}` : '—'}
                </td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#8b5cf6' }}>
                  {a.lostToPartial > 0 ? `-${fmt(a.lostToPartial)}` : '—'}
                </td>
                <td style={{ padding: '0.6rem 0.75rem', color: '#ef4444', fontWeight: 700 }}>
                  {a.lostRevenue > 0 ? `-${fmt(a.lostRevenue)}` : '—'}
                </td>
                <td style={{ padding: '0.6rem 0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, minWidth: 50 }}>
                      <div style={{
                        width: `${Math.min(100, a.utilizationPercent)}%`,
                        height: '100%',
                        background: a.utilizationPercent > 70 ? '#10b981' : a.utilizationPercent > 40 ? '#f59e0b' : '#ef4444',
                        borderRadius: 3
                      }} />
                    </div>
                    <span style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{a.utilizationPercent.toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function ClientsPanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.clients;
  const [search, setSearch] = useState('');
  if (!d) return <EmptyState />;

  const filtered = d.clients.filter(c =>
    [c.customerFullName, c.customerPhone, c.customerEmail].some(f =>
      f.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <StatCard label={t('reporting.clients.totalClients')}  value={`${d.clients.length}`}                                   color="#6366f1" />
        <StatCard label={t('reporting.clients.totalBookings')} value={`${d.clients.reduce((s, c) => s + c.totalBookings, 0)}`} color="#8b5cf6" />
        <StatCard label={t('reporting.clients.totalRevenue')}  value={fmt(d.clients.reduce((s, c) => s + c.totalSpent, 0))}   color="#10b981" />
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
          <SectionTitle title={`${t('reporting.clients.title')} (${filtered.length})`} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('reporting.clients.search')}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem 0.75rem', fontSize: '0.85rem', outline: 'none', width: 220 }}
            />
            <button
              onClick={() => reportingStore.exportClientsCsv(d)}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '0.4rem 1rem', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('reporting.clients.exportCsv')}
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  t('reporting.clients.name'),
                  t('reporting.clients.phone'),
                  t('reporting.clients.email'),
                  t('reporting.clients.totalBookings'),
                  t('reporting.clients.spent'),
                  t('reporting.clients.lastBooking'),
                  t('reporting.clients.favoriteAsset'),
                ].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff' }}>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{c.customerFullName || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{c.customerPhone}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{c.customerEmail || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                    <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 20, padding: '2px 10px', fontWeight: 700, fontSize: '0.78rem' }}>{c.totalBookings}</span>
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{fmt(c.totalSpent)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>{new Date(c.lastBookingDate).toLocaleDateString('en-GB')}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{c.mostUsedAssetName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BookingsOverviewPanel() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const d = reportingStore.bookingsOverview;
  const [search, setSearch] = useState('');
  if (!d) return <EmptyState />;

  const filtered = d.bookings.filter(b =>
    [b.customerFullName, b.customerPhone, b.assetName].some(f =>
      f.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <StatCard label={t('reporting.bookings.totalBookings')} value={`${d.totalBookings}`}           color="#6366f1" />
        <StatCard label={t('reporting.bookings.recurring')}     value={`${d.recurringBookings}`}       sub={`${d.singleBookings} ${t('reporting.bookings.single')}`} color="#8b5cf6" />
        <StatCard label={t('reporting.bookings.avgDuration')}   value={`${d.avgDurationMinutes} min`}  color="#f59e0b" />
      </div>

      {d.byStatus.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <SectionTitle title={t('reporting.bookings.byStatus')} />
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={d.byStatus} cx="50%" cy="50%" outerRadius={70} dataKey="count"
                nameKey="statusName"
                label={({ statusName, percent }: { statusName: string; percent: number }) => `${statusName} ${(percent * 100).toFixed(0)}%`}>
                {d.byStatus.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <SectionTitle title={`${t('reporting.bookings.title')} (${filtered.length})`} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('reporting.bookings.search')}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem 0.75rem', fontSize: '0.85rem', outline: 'none', width: 180 }}
            />
            <button
              onClick={() => reportingStore.exportBookingsCsv(d)}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '0.4rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}
            >{t('reporting.bookings.exportCsv')}</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  t('reporting.bookings.customer'),
                  t('reporting.clients.phone'),
                  t('reporting.bookings.asset'),
                  t('reporting.bookings.date'),
                  t('reporting.bookings.duration'),
                  t('reporting.bookings.capacity'),
                  t('reporting.bookings.price'),
                  t('reporting.bookings.payment'),
                  t('reporting.bookings.status'),
                ].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((b, i) => {
                const start = new Date(b.dateTimeStart);
                const end   = new Date(b.dateTimeEnd);
                const dur   = Math.round((end.getTime() - start.getTime()) / 60000);
                return (
                  <tr key={b.bookingId} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>
                      {b.customerFullName || '—'}
                      {b.isRecurring && (
                        <span style={{ marginLeft: 4, fontSize: '0.68rem', background: '#eef2ff', color: '#6366f1', borderRadius: 10, padding: '1px 5px' }}>
                          ↺ {t('reporting.bookings.recurringBadge')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{b.customerPhone}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{b.assetName}</td>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>
                      {start.toLocaleDateString('en-GB')} {start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{dur} min</td>
                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>{b.capacityUsed}</td>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{fmt(b.priceFinal)}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}><PayBadge isPayed={b.isPayed} /></td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>{b.currentStatus || t('reporting.bookings.noStatus')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 100 && (
            <div style={{ textAlign: 'center', padding: '0.5rem', color: '#9ca3af', fontSize: '0.8rem' }}>
              {t('reporting.bookings.showingFirst', { count: 100, total: filtered.length })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Reports Component ───────────────────────────────────────────────────

const REPORT_TAB_DEFS: { key: ActiveReport; tKey: string; icon: string }[] = [
  { key: 'occupancy',        tKey: 'reporting.tabs.occupancy',        icon: '📊' },
  { key: 'revenue',          tKey: 'reporting.tabs.revenue',          icon: '💰' },
  { key: 'payments',         tKey: 'reporting.tabs.payments',         icon: '💳' },
  { key: 'lostRevenue',      tKey: 'reporting.tabs.lostRevenue',      icon: '📉' },
  { key: 'clients',          tKey: 'reporting.tabs.clients',          icon: '👥' },
  { key: 'bookingsOverview', tKey: 'reporting.tabs.bookingsOverview', icon: '📋' },
];

const LOADER_MAP: Record<ActiveReport, (store: any) => any> = {
  occupancy:        s => s.loadOccupancy,
  revenue:          s => s.loadRevenue,
  payments:         s => s.loadPayments,
  lostRevenue:      s => s.loadLostRevenue,
  clients:          s => s.loadClients,
  bookingsOverview: s => s.loadBookingsOverview,
};

export default observer(function Reports() {
  const { reportingStore } = useStore();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [activeReport, setActiveReport] = useState<ActiveReport>('occupancy');
  const [startDate, setStartDate] = useState(toDateInput(firstOfMonth));
  const [endDate,   setEndDate]   = useState(toDateInput(today));
  const [venueId,   setVenueId]   = useState('');
  const [tabsOpen,  setTabsOpen]  = useState(false);

  const isLoading    = reportingStore.loading[activeReport];
  const activeTabDef = REPORT_TAB_DEFS.find(tab => tab.key === activeReport)!;

  const handleLoad = useCallback(async () => {
    const params: ReportQueryParams = {
      startDate: fromDateInput(startDate),
      endDate:   fromDateInput(endDate),
      ...(venueId && { companySportVenueId: venueId }),
    };
    await LOADER_MAP[activeReport](reportingStore)(params);
  }, [activeReport, startDate, endDate, venueId, reportingStore]);

  const renderPanel = () => {
    if (isLoading) return <LoadingSpinner />;
    switch (activeReport) {
      case 'occupancy':        return <OccupancyPanel />;
      case 'revenue':          return <RevenuePanel />;
      case 'payments':         return <PaymentsPanel />;
      case 'lostRevenue':      return <LostRevenuePanel />;
      case 'clients':          return <ClientsPanel />;
      case 'bookingsOverview': return <BookingsOverviewPanel />;
    }
  };

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Filter bar ── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '0.875rem' : '1rem 1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '1rem',
      }}>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280' }}>{t('reporting.filters.from')}</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.45rem 0.5rem', fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280' }}>{t('reporting.filters.to')}</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.45rem 0.5rem', fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <button onClick={handleLoad} disabled={isLoading} style={{
              background: isLoading ? '#a5b4fc' : '#6366f1', color: '#fff',
              border: 'none', borderRadius: 8, padding: '0.6rem',
              fontSize: '0.92rem', fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%',
            }}>
              {isLoading ? t('reporting.loading') : t('reporting.loadReport')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>{t('reporting.filters.from')}</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.88rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>{t('reporting.filters.to')}</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.88rem' }} />
            </div>
            <button onClick={handleLoad} disabled={isLoading} style={{
              background: isLoading ? '#a5b4fc' : '#6366f1', color: '#fff',
              border: 'none', borderRadius: 8, padding: '0.45rem 1.25rem',
              fontSize: '0.9rem', fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer', alignSelf: 'flex-end'
            }}>
              {isLoading ? t('reporting.loading') : t('reporting.loadReport')}
            </button>
          </div>
        )}
      </div>

      {/* ── Report tabs ── */}
      {isMobile ? (
        <div style={{ marginBottom: '1rem', position: 'relative' }}>
          <button onClick={() => setTabsOpen(o => !o)} style={{
            width: '100%', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10,
            padding: '0.65rem 1rem', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>{activeTabDef.icon} {t(activeTabDef.tKey)}</span>
            <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>{tabsOpen ? '▲' : '▼'}</span>
          </button>
          {tabsOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              zIndex: 50, overflow: 'hidden', border: '1px solid #e5e7eb',
            }}>
              {REPORT_TAB_DEFS.map((tab, idx) => (
                <button key={tab.key} onClick={() => { setActiveReport(tab.key); setTabsOpen(false); }} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.75rem 1rem',
                  background: activeReport === tab.key ? '#eef2ff' : '#fff',
                  color: activeReport === tab.key ? '#6366f1' : '#374151',
                  border: 'none', borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none',
                  fontSize: '0.9rem', fontWeight: activeReport === tab.key ? 700 : 500,
                  cursor: 'pointer', textAlign: 'left',
                }}>
                  <span>{tab.icon}</span>
                  <span>{t(tab.tKey)}</span>
                  {activeReport === tab.key && <span style={{ marginLeft: 'auto', color: '#6366f1' }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', overflowX: 'auto', gap: '0.5rem', marginBottom: '1.25rem', paddingBottom: '0.25rem', scrollbarWidth: 'none' }}>
          {REPORT_TAB_DEFS.map(tab => (
            <button key={tab.key} onClick={() => setActiveReport(tab.key)} style={{
              background: activeReport === tab.key ? '#6366f1' : '#fff',
              color: activeReport === tab.key ? '#fff' : '#374151',
              border: `1px solid ${activeReport === tab.key ? '#6366f1' : '#e5e7eb'}`,
              borderRadius: 30, padding: '0.4rem 1rem', fontSize: '0.85rem',
              fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s'
            }}>
              {tab.icon} {t(tab.tKey)}
            </button>
          ))}
        </div>
      )}

      {/* ── Panel ── */}
      {renderPanel()}
    </div>
  );
});